import { useRef, useState, Activity, useEffect } from 'react';

import { IconPlus, IconPencil } from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import CustomTable from '@/components/Table';
import { EAdminAccessStatus } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';
import { getRoleByID, getRolesList } from '@/service/rolesServices';
import useCommonStore from '@/store/useCommonStore';
import { useSettingsStore } from '@/store/useSettingsStore';

import AddRoleDrawer from '../modal/AddRoleDrawer';

type TAccessLevel = EAdminAccessStatus;

interface IRole {
  id: string;
  name: string;
  description: string;
  access_level: TAccessLevel;
}

interface IRoleEditData {
  id: string;
  role: string;
  description: string;
  sections: {
    id: string;
    has_access: boolean;
  }[];
}

interface IRolesProps {
  renderAction?: (action: React.ReactNode) => void;
}

const Roles = ({ renderAction }: IRolesProps) => {
  const { formatText } = useFormatText();
  const language = useSettingsStore((state) => state.settings.language);
  const hasRenderedAction = useRef(false);
  const [isRoleDrawerOpen, setIsRoleDrawerOpen] = useState(false);
  const [editRoleData, setEditRoleData] = useState<IRoleEditData | undefined>(
    undefined
  );
  const [roleName, setRoleName] = useState('');
  const [rolesData, setRolesData] = useState<IRoleEditData[]>([]);
  const { loadingTable, setLoadingTable } = useCommonStore();

  useEffect(() => {
    if (renderAction) {
      hasRenderedAction.current = true;
      renderAction(
        <Button
          label={formatText('settings.roles.add-role')}
          icon={<IconPlus size={20} />}
          onClick={handleAddRole}
        />
      );
    }

    // Cleanup: remove action when unmounting
    return () => {
      if (renderAction) {
        hasRenderedAction.current = false;
        renderAction(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditRole = async (roleId: string) => {
    try {
      const response = await getRoleByID(roleId);
      if (response?.status) {
        setEditRoleData({
          id: response?.data?.id,
          role: response?.data?.name,
          description: response?.data?.description,
          sections: response?.data?.sections,
        });
        setIsRoleDrawerOpen(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddRole = () => {
    setEditRoleData(undefined);
    setIsRoleDrawerOpen(true);
  };

  const handleCloseDrawer = (isOpen: boolean) => {
    setIsRoleDrawerOpen(isOpen);
    if (!isOpen) {
      setEditRoleData(undefined);
    }
  };

  const getAccessLevelBadge = (level: TAccessLevel) => {
    if (level === EAdminAccessStatus.FULL_ACCESS) {
      return (
        <span className="bg-success-light-50 text-success-secondary rounded-full text-xs font-semibold px-2 py-0.5">
          {formatText('settings.roles.full-access')}
        </span>
      );
    } else if (level === EAdminAccessStatus.NO_ACCESS) {
      return (
        <span className="bg-warning-light-50 text-warning-secondary rounded-full text-xs font-semibold px-2 py-0.5">
          {formatText('settings.roles.no-access')}
        </span>
      );
    }

    return (
      <span className="bg-indigo-light-50 text-indigo-secondary rounded-full text-xs font-semibold px-2 py-0.5">
        {formatText('settings.roles.limited-access')}
      </span>
    );
  };

  const fetchRoles = async () => {
    setLoadingTable(true);
    const response = await getRolesList();
    if (response?.status) {
      setRolesData(response?.data || []);
    }
    setLoadingTable(false);
  };

  const columns = [
    {
      key: 'role',
      title: 'settings.roles.column.role',
      render: (record: IRole) => (
        <span className="text-base font-semibold text-neutral-primary capitalize">
          {record?.name}
        </span>
      ),
    },
    {
      key: 'description',
      title: 'settings.roles.column.description',
      render: (record: IRole) => (
        <span className="text-sm text-neutral-secondary">
          {record?.description ?? '-'}
        </span>
      ),
    },
    {
      key: 'accessLevel',
      title: 'settings.roles.column.access-level',
      render: (record: IRole) => (
        <span className="text-nowrap">
          {getAccessLevelBadge(record?.access_level)}
        </span>
      ),
    },
    {
      key: 'action',
      title: 'settings.roles.column.action',
      render: (record: IRole) => (
        <button
          type="button"
          className="p-2 hover:bg-neutral-light-50 rounded-lg transition-colors cursor-pointer"
          onClick={() => {
            handleEditRole(record.id);
            setRoleName(record?.name);
          }}
        >
          <IconPencil size={20} className="text-neutral-secondary" />
        </button>
      ),
    },
  ];

  return (
    <div>
      <CustomTable
        dataSource={rolesData}
        columns={columns}
        rowKey="id"
        hidePagination
        rootClassName="h-auto!"
        isLoading={loadingTable}
        isEmpty={rolesData?.length === 0 && !loadingTable}
        emptyClassName="h-auto! py-10!"
      />
      <Activity mode={isRoleDrawerOpen ? 'visible' : 'hidden'}>
        <AddRoleDrawer
          setIsOpen={handleCloseDrawer}
          editData={editRoleData}
          fetchRoles={fetchRoles}
          roleName={roleName}
          setRoleName={setRoleName}
        />
      </Activity>
    </div>
  );
};

export default Roles;
